import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import './Auth.css';

const useQuery = () => {
  const location = useLocation();
  return useMemo(() => new URLSearchParams(location.search), [location.search]);
};

const ResetPassword = () => {
  const query = useQuery();
  const navigate = useNavigate();

  const tokenFromUrl = (query.get('token') || '').trim();

  const [email, setEmail] = useState('');
  const [emailLocked, setEmailLocked] = useState(false);
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState(tokenFromUrl);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!tokenFromUrl) {
      return;
    }
    let mounted = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const resp = await apiService.activatePasswordReset(tokenFromUrl);
        if (!mounted) return;
        setResetToken(resp.data.reset_token || tokenFromUrl);
        setEmail(resp.data.email || '');
        setEmailLocked(true);
        setMessage('Email подтверждён. Установите новый пароль.');
      } catch (err) {
        if (!mounted) return;
        const data = err.response?.data;
        setError(data?.error || data?.detail || 'Ссылка недействительна. Запросите восстановление заново.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [tokenFromUrl]);

  const handleConfirmCode = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const resp = await apiService.confirmPasswordReset((email || '').trim().toLowerCase(), code);
      setResetToken(resp.data.reset_token);
      setEmail(resp.data.email || email);
      setEmailLocked(true);
      setMessage('Код подтверждён. Установите новый пароль.');
    } catch (err) {
      const data = err.response?.data;
      setError(data?.error || data?.detail || 'Неверный код или срок действия истёк.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const resp = await apiService.resetPassword({
        reset_token: resetToken,
        new_password: password,
        new_password_confirm: passwordConfirm,
      });
      setMessage(resp.data?.message || 'Пароль изменён.');
      setTimeout(() => navigate('/login'), 800);
    } catch (err) {
      const data = err.response?.data;
      const msg =
        data?.error ||
        (data?.new_password && (Array.isArray(data.new_password) ? data.new_password.join(' ') : String(data.new_password))) ||
        (data?.new_password_confirm && (Array.isArray(data.new_password_confirm) ? data.new_password_confirm.join(' ') : String(data.new_password_confirm))) ||
        data?.detail ||
        err.message ||
        'Не удалось сменить пароль.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const needsToken = !resetToken;

  return (
    <div className="auth-container">
      <div className="auth-form">
        <h2>Смена пароля</h2>

        {error && <div className="error-message">{error}</div>}
        {message && <div className="success-message">{message}</div>}

        {needsToken ? (
          <form onSubmit={handleConfirmCode}>
            <div className="form-group">
              <label htmlFor="email">Email:</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="code">Код из письма:</label>
              <input
                type="text"
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                disabled={loading}
              />
            </div>
            <button type="submit" disabled={loading}>
              {loading ? 'Проверка…' : 'Подтвердить код'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleReset}>
            <div className="form-group">
              <label htmlFor="email">Email:</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading || emailLocked}
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Новый пароль:</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="passwordConfirm">Повторите новый пароль:</label>
              <input
                type="password"
                id="passwordConfirm"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                required
                disabled={loading}
              />
            </div>
            <button type="submit" disabled={loading}>
              {loading ? 'Сохранение…' : 'Сменить пароль'}
            </button>
          </form>
        )}

        <p className="auth-link">
          Нет письма? <Link to="/forgot-password">Запросить код</Link>
        </p>
        <p className="auth-link">
          <Link to="/login">Вернуться ко входу</Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;

